import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarSearch,
} from 'kbar'
import type { ReactNode } from 'react'
import { AllActions } from './actions/all-actions'
import {
  CommandPaletteProvider,
  PaletteController,
  useCommandPaletteState,
} from './context'
import {
  FollowUpHeader,
  MergeHeader,
  MergePreview,
  RemovalHeader,
  SelectSeriesHeader,
  SeriesHeader,
} from './headers'
import { RenderResults } from './render-results'
import { animatorStyle, positionerStyle, searchStyle } from './styles'
import type { SeriesProblemDisplay } from './types'

// Re-export the public API hook
export { useCommandPalette } from './context'

interface ProblemsCommandPaletteProps {
  children: ReactNode
}

/**
 * Command Palette Header component that displays context-specific headers
 * based on the current mode.
 */
function CommandPaletteHeader() {
  const {
    mode,
    selectedSeries,
    seriesProblems,
    lockedSeriesId,
    problemsToRemove,
    allSeries,
    mergePhase,
    mergePrimaryProblem,
    mergeDuplicateProblem,
    followUpPhase,
    followUpTargetProblem,
    followUpExistingFollowUps,
    followUpInput,
  } = useCommandPaletteState()

  // Build display problems for series header
  const displayProblems: SeriesProblemDisplay[] = []

  if (mode === 'add-to-series' && selectedSeries) {
    for (const member of selectedSeries.members) {
      displayProblems.push({
        id: member.problemId,
        name: member.problemTitle,
        externalId: member.problemExternalId,
        isExisting: true,
      })
    }
  }

  for (const p of seriesProblems) {
    displayProblems.push({
      id: p.id,
      name: p.name,
      externalId: p.externalId,
      sourceIcon: p.sourceIcon,
      source: p.source,
      isNew: true,
    })
  }

  const showSeriesHeader = mode === 'create-series' || mode === 'add-to-series'
  const headerMode = mode === 'create-series' ? 'create' : 'add'
  const canSave =
    mode === 'create-series'
      ? seriesProblems.length >= 2
      : seriesProblems.length >= 1

  return (
    <>
      {showSeriesHeader && (
        <SeriesHeader
          problems={displayProblems}
          mode={headerMode}
          seriesName={selectedSeries?.name}
          canSave={canSave}
          newCount={seriesProblems.length}
        />
      )}
      {mode === 'remove-from-series' && (
        <RemovalHeader
          problems={problemsToRemove}
          seriesName={
            lockedSeriesId
              ? allSeries.find((s) => s.id === lockedSeriesId)?.name
              : undefined
          }
          canRemove={problemsToRemove.length >= 1}
        />
      )}
      {mode === 'select-series' && <SelectSeriesHeader />}
      {mode === 'merge' && (
        <MergeHeader
          primaryProblem={mergePrimaryProblem}
          duplicateProblem={mergeDuplicateProblem}
          mergePhase={mergePhase}
        />
      )}
      {mode === 'merge' && mergePhase === 'preview' && <MergePreview />}
      {mode === 'add-follow-up' && (
        <FollowUpHeader
          targetProblem={followUpTargetProblem}
          existingFollowUps={followUpExistingFollowUps}
          followUpPhase={followUpPhase}
          followUpInput={followUpInput}
        />
      )}
    </>
  )
}

/**
 * Determines the placeholder text based on the current mode.
 */
function usePlaceholder() {
  const { mode, mergePhase, lockedSeriesId, followUpPhase } =
    useCommandPaletteState()

  if (mode === 'select-series') {
    return 'Search for a series...'
  }
  if (mode === 'create-series' || mode === 'add-to-series') {
    return 'Search problems to add...'
  }
  if (mode === 'remove-from-series') {
    return lockedSeriesId
      ? 'Search problems in this series to remove...'
      : 'Search problems in any series to remove...'
  }
  if (mode === 'merge') {
    if (mergePhase === 'select-primary') {
      return 'Search for the CANONICAL problem...'
    }
    if (mergePhase === 'select-duplicate') {
      return 'Search for the DUPLICATE from another source...'
    }
    return ''
  }
  if (mode === 'add-follow-up') {
    if (followUpPhase === 'select-problem') {
      return 'Search for a problem to add a follow-up to...'
    }
    if (followUpPhase === 'enter-prompt') {
      return 'Enter the follow-up prompt (e.g., "Can you do it in O(1) space?")...'
    }
    if (followUpPhase === 'enter-time-complexity') {
      return 'Enter time complexity (e.g., "O(n)") or press Enter to skip...'
    }
    if (followUpPhase === 'enter-space-complexity') {
      return 'Enter space complexity (e.g., "O(1)") or press ⌘+Enter to save...'
    }
  }
  return 'Type a command...'
}

/**
 * Inner component that uses the context for placeholder.
 */
function CommandPaletteSearch() {
  const placeholder = usePlaceholder()

  return (
    <KBarSearch
      id="kbar-search-input"
      style={searchStyle}
      placeholder={placeholder}
    />
  )
}

/**
 * Main command palette provider component.
 * Wraps children with KBar and command palette context.
 */
export function ProblemsCommandPalette({
  children,
}: ProblemsCommandPaletteProps) {
  return (
    <KBarProvider>
      <CommandPaletteProvider>
        {/* Controller to handle pending actions that need useKBar */}
        <PaletteController />

        <KBarPortal>
          <KBarPositioner style={positionerStyle}>
            <KBarAnimator style={animatorStyle}>
              <CommandPaletteHeader />
              <CommandPaletteSearch />
              <RenderResults />
            </KBarAnimator>
          </KBarPositioner>
        </KBarPortal>

        {/* Single action module that registers all actions */}
        <AllActions />

        {children}
      </CommandPaletteProvider>
    </KBarProvider>
  )
}
